export type CropPoint = { x: number; y: number }
export type CropRectangle = CropPoint & { width: number; height: number }

export type CropDimensions = {
  displayWidth: number
  displayHeight: number
  naturalWidth: number
  naturalHeight: number
}

export type CropFailureReason = 'invalid-selection' | 'decode-failed' | 'canvas-failed'

type DecodedImage = { image: CanvasImageSource | object; width: number; height: number }
type CanvasAdapter = {
  drawImage: (...args: unknown[]) => void
  toDataURL: (type: string, quality: number) => string
}

export type ApplyCropImageArgs = {
  source: string
  selection: CropRectangle
  dimensions: CropDimensions
  decode?: (source: string) => Promise<DecodedImage>
  createCanvas?: (width: number, height: number) => CanvasAdapter
}

export function normalizeCropRectangle(a: CropPoint, b: CropPoint): CropRectangle {
  const x = Math.min(a.x, b.x)
  const y = Math.min(a.y, b.y)
  return {
    x,
    y,
    width: Math.max(a.x, b.x) - x,
    height: Math.max(a.y, b.y) - y,
  }
}

export function moveCropRectangle(
  rectangle: CropRectangle,
  key: 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight',
  accelerated: boolean,
): CropRectangle {
  const step = accelerated ? 10 : 1
  const delta = {
    ArrowUp: { x: 0, y: -step },
    ArrowDown: { x: 0, y: step },
    ArrowLeft: { x: -step, y: 0 },
    ArrowRight: { x: step, y: 0 },
  }[key]
  return { ...rectangle, x: rectangle.x + delta.x, y: rectangle.y + delta.y }
}

export function scaleCropRectangle(
  selection: CropRectangle,
  dimensions: CropDimensions,
): { ok: true; rect: CropRectangle } | { ok: false; reason: 'invalid-selection' } {
  const { displayWidth, displayHeight, naturalWidth, naturalHeight } = dimensions
  if (
    selection.width <= 0
    || selection.height <= 0
    || displayWidth <= 0
    || displayHeight <= 0
    || naturalWidth <= 0
    || naturalHeight <= 0
  ) {
    return { ok: false, reason: 'invalid-selection' }
  }

  const startX = Math.max(0, Math.min(displayWidth, selection.x))
  const startY = Math.max(0, Math.min(displayHeight, selection.y))
  const endX = Math.max(0, Math.min(displayWidth, selection.x + selection.width))
  const endY = Math.max(0, Math.min(displayHeight, selection.y + selection.height))
  if (endX <= startX || endY <= startY) {
    return { ok: false, reason: 'invalid-selection' }
  }

  const scaleX = naturalWidth / displayWidth
  const scaleY = naturalHeight / displayHeight
  return {
    ok: true,
    rect: {
      x: Math.round(startX * scaleX),
      y: Math.round(startY * scaleY),
      width: Math.round((endX - startX) * scaleX),
      height: Math.round((endY - startY) * scaleY),
    },
  }
}

function decodeBrowserImage(source: string): Promise<DecodedImage> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve({
      image,
      width: image.naturalWidth,
      height: image.naturalHeight,
    })
    image.onerror = () => reject(new Error('image decode failed'))
    image.src = source
  })
}

function createBrowserCanvas(width: number, height: number): CanvasAdapter {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  if (!context) throw new Error('canvas context unavailable')
  return {
    drawImage: (...args: unknown[]) => {
      context.drawImage(...(args as Parameters<CanvasRenderingContext2D['drawImage']>))
    },
    toDataURL: (type, quality) => canvas.toDataURL(type, quality),
  }
}

export async function applyCropImage(args: ApplyCropImageArgs): Promise<
  | { ok: true; dataUrl: string; rect: CropRectangle }
  | { ok: false; reason: CropFailureReason; dataUrl: string }
> {
  const geometry = scaleCropRectangle(args.selection, args.dimensions)
  if ('reason' in geometry) {
    return { ok: false, reason: geometry.reason, dataUrl: args.source }
  }

  let decoded: DecodedImage
  try {
    decoded = await (args.decode ?? decodeBrowserImage)(args.source)
  } catch {
    return { ok: false, reason: 'decode-failed', dataUrl: args.source }
  }

  try {
    const { x, y, width, height } = geometry.rect
    const canvas = (args.createCanvas ?? createBrowserCanvas)(width, height)
    canvas.drawImage(decoded.image, x, y, width, height, 0, 0, width, height)
    const dataUrl = canvas.toDataURL('image/jpeg', 0.7)
    if (!dataUrl.startsWith('data:image/jpeg')) throw new Error('invalid canvas output')
    return { ok: true, dataUrl, rect: geometry.rect }
  } catch {
    return { ok: false, reason: 'canvas-failed', dataUrl: args.source }
  }
}
